import { useEffect, useState } from 'react';
import './VouchersPage.css';
import { fetchVouchers } from '../services/api';

export default function VouchersPage({ apiKey, organizationId }) {
  const [vouchers, setVouchers] = useState(null);
  const [error, setError] = useState(null);

  // Filteri
  const [searchObject, setSearchObject] = useState('');
  const [searchUser, setSearchUser] = useState('');
  const [searchAmount, setSearchAmount] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');

  // Export kolone
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState({
    uuid: true,
    object_name: true,
    username: true,
    telephone: true,
    email: true,
    amount: true,
    date_creation: true,
    pay_out: true,
    amount_available: true,
    date_expiration: true,
    description: true
  });

  const columnLabels = {
    uuid: 'Kod vaučera',
    object_name: 'Objekat',
    username: 'Korisnik',
    telephone: 'Telefon',
    email: 'Email',
    amount: 'Iznos',
    date_creation: 'Datum kreiranja',
    pay_out: 'Iskorišćeno',
    amount_available: 'Dostupno',
    date_expiration: 'Datum isteka',
    description: 'Poruka'
  };

  useEffect(() => {
    if (!apiKey || !organizationId) return;

    fetchVouchers(apiKey, organizationId)
      .then(setVouchers)
      .catch(err => {
        console.error('Error:', err);
        setError(err.message);
      });
  }, [apiKey, organizationId]);

  if (error) return <p>❌ Greška: {error}</p>;
  if (!vouchers) return <p>⏳ Učitavanje vaučera...</p>;

    const filteredVouchers = vouchers.filter(v => {
      const matchesObject = v.object_name.toLowerCase().includes(searchObject.toLowerCase());
      const matchesUser = v.username.toLowerCase().includes(searchUser.toLowerCase());
      const matchesAmount = searchAmount === '' || v.amount.toString().includes(searchAmount);

      // Filter za datum isteka
      let matchesExpirationDate = true;
      if (dateFrom || dateTo) {
        const voucherDate = new Date(v.date_expiration);

        if (dateFrom && dateTo) {
          const from = new Date(dateFrom);
          const to = new Date(dateTo);
          matchesExpirationDate = voucherDate >= from && voucherDate <= to;
        } else if (dateFrom) {
          const from = new Date(dateFrom);
          matchesExpirationDate = voucherDate >= from;
        } else if (dateTo) {
          const to = new Date(dateTo);
          matchesExpirationDate = voucherDate <= to;
        }
      }

      // Filter za datum kreiranja
      let matchesCreatedDate = true;
      if (createdFrom || createdTo) {
        const createdDate = new Date(v.date_creation);

        if (createdFrom && createdTo) {
          const from = new Date(createdFrom);
          const to = new Date(createdTo);
          matchesCreatedDate = createdDate >= from && createdDate <= to;
        } else if (createdFrom) {
          const from = new Date(createdFrom);
          matchesCreatedDate = createdDate >= from;
        } else if (createdTo) {
          const to = new Date(createdTo);
          matchesCreatedDate = createdDate <= to;
        }
      }

      return matchesObject && matchesUser && matchesAmount && matchesExpirationDate && matchesCreatedDate;
    });

    const resetFilters = () => {
      setSearchObject('');
      setSearchUser('');
      setSearchAmount('');
      setDateFrom('');
      setDateTo('');
      setCreatedFrom('');
      setCreatedTo('');
    };

  const toggleColumn = (column) => {
    setSelectedColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const selectAllColumns = () => {
    const allSelected = {};
    Object.keys(selectedColumns).forEach(key => {
      allSelected[key] = true;
    });
    setSelectedColumns(allSelected);
  };

  const deselectAllColumns = () => {
    const noneSelected = {};
    Object.keys(selectedColumns).forEach(key => {
      noneSelected[key] = false;
    });
    setSelectedColumns(noneSelected);
  };

  const exportToCSV = () => {
    if (filteredVouchers.length === 0) {
      alert('Nema podataka za export');
      return;
    }

    const selectedCols = Object.keys(selectedColumns).filter(col => selectedColumns[col]);

    if (selectedCols.length === 0) {
      alert('Molimo odaberite bar jednu kolonu');
      return;
    }

    // Kreiraj CSV header
    const headers = selectedCols.map(col => columnLabels[col]).join(',');

    // Kreiraj CSV redove
    const rows = filteredVouchers.map(v => {
      return selectedCols.map(col => {
        let value = v[col];

        // Format brojeva
        if (col === 'amount' || col === 'pay_out' || col === 'amount_available') {
          value = value.toFixed(2);
        }

        // Escape vrednosti koje sadrže zareze
        if (typeof value === 'string' && value.includes(',')) {
          value = `"${value}"`;
        }

        return value;
      }).join(',');
    });

    // Kombinuj header i redove
    const csvContent = [headers, ...rows].join('\n');

    // Dodaj UTF-8 BOM
    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;

    // Kreiraj Blob sa UTF-8 encodingom
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `vauceri_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setShowExportModal(false);
  };

  return (
    <div className="vouchers">
      <h2>🎟️ Svi vaučeri ({vouchers.length})</h2>

    {/* Filter sekcija */}
    <div className="filters-container">
      <div className="filter-group">
        <label>🏢 Objekat:</label>
        <input
          type="text"
          placeholder="Pretraži po objektu..."
          value={searchObject}
          onChange={(e) => setSearchObject(e.target.value)}
        />
      </div>

      <div className="filter-group">
        <label>👤 Korisnik:</label>
        <input
          type="text"
          placeholder="Pretraži po korisniku..."
          value={searchUser}
          onChange={(e) => setSearchUser(e.target.value)}
        />
      </div>

      <div className="filter-group">
        <label>💰 Iznos:</label>
        <input
          type="text"
          placeholder="Pretraži po iznosu..."
          value={searchAmount}
          onChange={(e) => setSearchAmount(e.target.value)}
        />
      </div>

      <div className="filter-group">
        <label>📅 Ističe od:</label>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
        />
      </div>

      <div className="filter-group">
        <label>📅 Ističe do:</label>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
        />
      </div>

      <div className="filter-group">
        <label>🆕 Kreiran od:</label>
        <input
          type="date"
          value={createdFrom}
          onChange={(e) => setCreatedFrom(e.target.value)}
        />
      </div>

      <div className="filter-group">
        <label>🆕 Kreiran do:</label>
        <input
          type="date"
          value={createdTo}
          onChange={(e) => setCreatedTo(e.target.value)}
        />
      </div>

      <button className="reset-button" onClick={resetFilters}>
        🔄 Resetuj
      </button>
    </div>

      <p className="filter-results">
        Prikazano: <strong>{filteredVouchers.length}</strong> od {vouchers.length} vaučera
      </p>

      {/* Export Modal */}
      {showExportModal && (
        <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Odaberi kolone za export</h3>

            <div className="modal-actions">
              <button onClick={selectAllColumns} className="select-all-btn">
                Odaberi sve
              </button>
              <button onClick={deselectAllColumns} className="deselect-all-btn">
                Poništi sve
              </button>
            </div>

            <div className="columns-list">
              {Object.keys(selectedColumns).map(col => (
                <label key={col} className="column-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedColumns[col]}
                    onChange={() => toggleColumn(col)}
                  />
                  <span>{columnLabels[col]}</span>
                </label>
              ))}
            </div>

            <div className="modal-buttons">
              <button
                className="cancel-btn"
                onClick={() => setShowExportModal(false)}
              >
                Otkaži
              </button>
              <button
                className="confirm-btn"
                onClick={exportToCSV}
              >
                📥 Preuzmi CSV
              </button>
            </div>
          </div>
        </div>
      )}

      {filteredVouchers.length === 0 ? (
        <p>Nema vaučera koji odgovaraju filterima</p>
      ) : (
        <>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Kod vaučera</th>
                  <th>Objekat</th>
                  <th>Korisnik</th>
                  <th>Telefon</th>
                  <th>Email</th>
                  <th>Iznos</th>
                  <th>Kreiran</th>
                  <th>Iskorišćeno</th>
                  <th>Dostupno</th>
                  <th>Ističe</th>
                  <th>Poruka</th>
                </tr>
              </thead>
              <tbody>
                {filteredVouchers.map(v => {
                  const expirationDate = new Date(v.date_expiration);
                  expirationDate.setHours(23, 59, 59, 999);
                  const creationDate = new Date(v.date_creation);
                  const isExpired = expirationDate < new Date();
                  const today = new Date();
                  const sevenDaysFromNow = new Date(today);
                  sevenDaysFromNow.setDate(today.getDate() + 7);
                  const expiresSoon = !isExpired && expirationDate <= sevenDaysFromNow;

                  return (
                    <tr key={v.uuid}>
                      <td
                          style={{ fontSize: '11px', fontFamily: 'monospace', cursor: 'pointer' }}
                          title={v.uuid}
                      >
                          {v.uuid.slice(0, 8)}...
                      </td>
                      <td>{v.object_name}</td>
                      <td>{v.username}</td>
                      <td>{v.telephone}</td>
                      <td>{v.email}</td>
                      <td>{v.amount.toFixed(2)} RSD</td>
                      <td>
                        <span
                          className={'status-active'}
                        >
                        {creationDate.toLocaleDateString('sr-RS')}
                        </span>
                      </td>
                      <td>{v.pay_out.toFixed(2)} RSD</td>
                      <td>
                        <strong>{v.amount_available.toFixed(2)} RSD</strong>
                      </td>
                      <td>
                        <span
                          className={
                            isExpired
                              ? 'status-inactive'
                              : expiresSoon
                              ? 'status-warning'
                              : 'status-active'
                          }
                        >
                          {expirationDate.toLocaleDateString('sr-RS')}
                        </span>
                      </td>
                      <td>{v.description}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <button
            className="export-button"
            onClick={() => setShowExportModal(true)}
            style={{ marginTop: '20px', width: '100%' }}
          >
            📥 Export u CSV ({filteredVouchers.length} vaučer/a)
          </button>
        </>
      )}
    </div>
  );
}